import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TenantLayout from '../components/TenantLayout';
import { useSite } from '../context/SiteContext';

const TenantAnalytics = () => {
  const { selectedSite, updateTrigger } = useSite();
  const [analytics, setAnalytics] = useState({
    totalBookings: 0,
    totalRevenue: 0,
    todayBookings: 0,
    todayRevenue: 0
  });

  useEffect(() => {
    fetchAnalytics();
  }, [selectedSite, updateTrigger]);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      // First get all bookings
      const response = await axios.get(`${API_BASE_URL}/api/bookings`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'x-tenant-id': '68fbc468677e7e1d89957085'
        }
      });
      let allBookings = response.data.bookings || [];
      
      // Filter by selected site
      if (selectedSite && selectedSite !== 'all') {
        allBookings = allBookings.filter(booking => {
          return booking.ticketTypeId?.siteId === selectedSite;
        });
        console.log(`Filtered analytics by site ${selectedSite}:`, allBookings.length, 'bookings');
      }
      
      // Calculate analytics from filtered bookings
      const today = new Date().toDateString();
      const todayBookings = allBookings.filter(booking => 
        new Date(booking.createdAt).toDateString() === today
      );
      
      const analytics = {
        totalBookings: allBookings.length,
        totalRevenue: allBookings.reduce((sum, booking) => sum + (booking.totalAmount || 0), 0),
        todayBookings: todayBookings.length,
        todayRevenue: todayBookings.reduce((sum, booking) => sum + (booking.totalAmount || 0), 0)
      };
      
      setAnalytics(analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics({ totalBookings: 0, totalRevenue: 0, todayBookings: 0, todayRevenue: 0 });
    }
  };

  return (
    <TenantLayout>
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '28px' }}>Analytics Dashboard</h1>
          <p style={{ color: '#718096', margin: 0 }}>Track performance and insights for your sites</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <div style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(20, 184, 166, 0.3)', color: 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>📋</span>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Total Bookings</h3>
            </div>
            <p style={{ fontSize: '32px', fontWeight: 'bold', margin: 0 }}>{analytics.totalBookings}</p>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)', color: 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>💰</span>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Total Revenue</h3>
            </div>
            <p style={{ fontSize: '32px', fontWeight: 'bold', margin: 0 }}>₹{analytics.totalRevenue}</p>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(8, 145, 178, 0.3)', color: 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>🌅</span>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Today's Bookings</h3>
            </div>
            <p style={{ fontSize: '32px', fontWeight: 'bold', margin: 0 }}>{analytics.todayBookings}</p>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #0d9488 0%, #065f46 100%)', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(13, 148, 136, 0.3)', color: 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>📈</span>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Today's Revenue</h3>
            </div>
            <p style={{ fontSize: '32px', fontWeight: 'bold', margin: 0 }}>₹{analytics.todayRevenue}</p>
          </div>
        </div>

        <div style={{ background: 'white', padding: '32px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <span style={{ fontSize: '32px' }}>📈</span>
            <h3 style={{ margin: 0, color: '#2d3748', fontSize: '24px', fontWeight: '700' }}>Detailed Analytics</h3>
          </div>
          <div style={{ 
            background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)', 
            padding: '24px', 
            borderRadius: '12px', 
            border: '1px solid #a7f3d0',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📉</div>
            <p style={{ color: '#047857', fontSize: '16px', fontWeight: '600' }}>Detailed analytics charts and reports will be displayed here.</p>
            <p style={{ color: '#065f46', fontSize: '14px', margin: '8px 0 0 0' }}>Coming soon with advanced insights and visualizations</p>
          </div>
        </div>
      </div>
    </TenantLayout>
  );
};

export default TenantAnalytics;